import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import api from "../chat/api";
import GoogleSignInButton from "../../components/GoogleSignInButton";
import { fetchAndCacheSubscriptionStatus } from "../../utils/subscriptionStatus";
import AuthCard from "./components/AuthCard";
import ProductDemoPanel from "./components/ProductDemoPanel";
import StepAbout from "./components/StepAbout";
import StepDetails from "./components/StepDetails";
import StepDone from "./components/StepDone";
import StepVerify from "./components/StepVerify";
import { FONT, GoogleButtonFace, isValidName, nameParts, useMediaQuery } from "./components/authFlowUi";
import "./components/authFlow.css";

const EMPTY_OTP = ["", "", "", "", "", ""];
const RESEND_SECONDS = 45;
const DONE_REDIRECT_MS = 2500;
const STEP_INDEX = { details: 0, verify: 1, about: 2, done: 3 };
const SEND_FAILED = "We couldn't send the code. Please try again.";

const clearSession = () => {
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith("rzp_")) localStorage.removeItem(key);
  });
  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (name.startsWith("rzp_")) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    }
  });
  Cookies.remove("access_token");
  Cookies.remove("refresh_token");
  Cookies.remove("selected_org_id");
  Cookies.remove("subscription_status");
  localStorage.clear();
  sessionStorage.clear();
};

const cacheWorkspaces = async (accessToken) => {
  try {
    const orgRes = await api.get("/api/v1/org/", { headers: { Authorization: `Bearer ${accessToken}` } });
    if (orgRes.status !== 200 || !Array.isArray(orgRes.data)) return;
    const orgs = orgRes.data;
    localStorage.setItem("cachedWorkspaces", JSON.stringify(orgs));
    const ownerOrg = orgs.find((org) => org.permission === "owner");
    const defaultOrg = ownerOrg || orgs[0] || null;
    if (defaultOrg) {
      localStorage.setItem("activeWorkspace", JSON.stringify(defaultOrg));
      localStorage.setItem("activeWorkspaceId", defaultOrg.id.toString());
    }
    if (ownerOrg) Cookies.set("selected_org_id", ownerOrg.id.toString(), { expires: 365 });
  } catch (err) {
    console.error("Error fetching workspaces during sign-in:", err);
  }
};

const otpErrorText = (serverMessage) => {
  if (serverMessage === "You already have an active subscription.")
    return "Promo codes only work on your first sign-in. Go back, remove the code and try again.";
  if (serverMessage && serverMessage.toLowerCase().includes("expired"))
    return "That code has expired. Request a new one below.";
  if (serverMessage === "Invalid coupon code.") return "That promo code isn't valid. Go back and check it.";
  if (serverMessage && serverMessage.startsWith("Too many attempts")) return serverMessage;
  return "That code didn't match. Check the latest email and try again.";
};

const Signin = ({ mode = "signin" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const mobile = useMediaQuery("(max-width: 1023px)");
  const narrow = useMediaQuery("(max-width: 479px)");
  const short = useMediaQuery("(max-height: 860px)");
  const veryShort = useMediaQuery("(max-height: 760px)");

  const [flow, setFlow] = useState(mode);
  const [step, setStep] = useState("details");
  const [name, setName] = useState("");
  const [email, setEmail] = useState(location.state?.email || "");
  const [notice, setNotice] = useState(location.state?.notice || "");
  const [promoCode, setPromoCode] = useState("");
  const [otp, setOtp] = useState(EMPTY_OTP);
  const [otpError, setOtpError] = useState("");
  const [otpErrorCount, setOtpErrorCount] = useState(0);
  const [secs, setSecs] = useState(RESEND_SECONDS);
  const [sendError, setSendError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [about, setAbout] = useState({ company: "", phone: "", role: "", heard: "", use: "", updates: true });
  const [aboutError, setAboutError] = useState("");
  const [skipped, setSkipped] = useState(false);
  const [askName, setAskName] = useState(false);
  const [googleName, setGoogleName] = useState("");

  useEffect(() => {
    const invitationToken = searchParams.get("invitation");
    if (invitationToken) Cookies.set("invitation_token", invitationToken, { expires: 1 });
  }, [searchParams]);

  useEffect(() => {
    clearSession();
  }, []);

  useEffect(() => {
    if (step !== "verify" || secs <= 0) return undefined;
    const id = setTimeout(() => setSecs((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [step, secs]);

  const finish = () => {
    if (Cookies.get("invitation_token")) navigate("/accept-invitation");
    else navigate("/rfp-dashboard");
  };

  useEffect(() => {
    if (step !== "done") return undefined;
    const id = setTimeout(finish, DONE_REDIRECT_MS);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const switchFlow = (extraState) => {
    const target = flow === "signup" ? "/signin" : "/signup";
    navigate(`${target}${location.search}`, { state: { email, ...extraState } });
  };

  const sendCode = async () => {
    setLoading(true);
    setSendError("");
    setNotice("");
    try {
      const payload = flow === "signup" ? { email, full_name: name.trim() } : { email, mode: "signin" };
      const res = await api.post("api/v1/accounts/send-otp/", payload);
      if (res.data?.message?.includes("OTP sent")) {
        setOtp(EMPTY_OTP);
        setOtpError("");
        setSecs(RESEND_SECONDS);
        setStep("verify");
      } else {
        setSendError(res.data?.message || SEND_FAILED);
      }
    } catch (err) {
      if (err?.response?.data?.code === "no_account") {
        switchFlow({ notice: "No account for this email yet. Add your name to create one." });
        return;
      }
      setSendError(err?.response?.data?.message || SEND_FAILED);
    } finally {
      setLoading(false);
    }
  };

  const failOtp = (serverMessage) => {
    setOtp(EMPTY_OTP);
    setOtpError(otpErrorText(serverMessage));
    setOtpErrorCount((n) => n + 1);
  };

  const verifyCode = async (code) => {
    if (loading) return;
    setLoading(true);
    const payload = { email, otp: code, referral: window.Rewardful?.referral };
    if (promoCode.trim()) payload.coupon_code = promoCode.trim();
    try {
      const res = await api.post("api/v1/accounts/verify/", payload);
      if (res.status !== 200 || !res.data.access_token) {
        failOtp(res.data?.message);
        return;
      }
      const { access_token, refresh_token, expires_on, full_name, needs_onboarding } = res.data;
      Cookies.set("access_token", access_token);
      Cookies.set("refresh_token", refresh_token);
      if (expires_on) Cookies.set("expires_on", expires_on);
      if (full_name) {
        localStorage.setItem("fullName", full_name);
        setName(full_name);
      }
      localStorage.setItem("email", email);

      await cacheWorkspaces(access_token);
      try {
        await fetchAndCacheSubscriptionStatus(api, { accessToken: access_token });
      } catch (subErr) {
        console.error("Error fetching subscription status during sign-in:", subErr);
      }

      if (needs_onboarding) {
        setFlow("signup");
        setStep("about");
      } else finish();
    } catch (err) {
      failOtp(err?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setOtp(EMPTY_OTP);
    setOtpError("");
    setSecs(RESEND_SECONDS);
    try {
      const res = await api.post("api/v1/accounts/resend-otp/", { email });
      if (res.data?.status && res.data.status !== 200) {
        setOtpError(res.data.message || SEND_FAILED);
        setSecs(0);
      }
    } catch (err) {
      setOtpError(err?.response?.data?.message || SEND_FAILED);
      setSecs(0);
    }
  };

  const nameToSave = askName && isValidName(name) ? name.trim() : null;

  const rememberProfile = ({ full_name, access_token, refresh_token } = {}) => {
    if (full_name) localStorage.setItem("fullName", full_name);
    if (access_token && refresh_token) {
      Cookies.set("access_token", access_token);
      Cookies.set("refresh_token", refresh_token);
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
    }
  };

  const saveAbout = async () => {
    setLoading(true);
    setAboutError("");
    try {
      const res = await api.patch("api/v1/accounts/profile/", {
        ...(nameToSave && { full_name: nameToSave }),
        company_name: about.company.trim(),
        phone: about.phone.trim(),
        role: about.role,
        heard_about: about.heard,
        use_case: about.use,
        marketing_opt_in: about.updates,
      });
      rememberProfile(res.data);
      setSkipped(false);
      setStep("done");
    } catch (err) {
      setAboutError("We couldn't save your details. You can skip for now and add them later on the Account page.");
    } finally {
      setLoading(false);
    }
  };

  const skipAbout = async () => {
    // Always tell the backend about the skip, so support still hears about the signup.
    setLoading(true);
    try {
      const res = await api.patch("api/v1/accounts/profile/", {
        skipped: true,
        ...(nameToSave && nameToSave !== googleName && { full_name: nameToSave }),
      });
      rememberProfile(res.data);
    } catch (err) {
      console.error("Could not save the skip:", err);
    } finally {
      setLoading(false);
    }
    setSkipped(true);
    setStep("done");
  };

  const onGoogleSuccess = ({ email: googleEmail, fullName, needsOnboarding }) => {
    setGoogleLoading(false);
    if (fullName) {
      localStorage.setItem("fullName", fullName);
      setName(fullName);
    }
    if (googleEmail) setEmail(googleEmail);
    if (needsOnboarding) {
      setAskName(true);
      setGoogleName(fullName || "");
      setFlow("signup");
      setStep("about");
    } else finish();
  };

  const shownName = askName && !isValidName(name) ? googleName : name;
  const { first, initials } = nameParts(shownName, email);
  const stepIndex = STEP_INDEX[step];
  const roomy = flow === "signin" && !mobile;
  const cardShort = short && !mobile && !roomy;

  const googleButton = (
    <GoogleSignInButton
      showError={(message) => {
        setGoogleLoading(false);
        setSendError(message);
      }}
      onStart={() => {
        setSendError("");
        setGoogleLoading(true);
      }}
      onSuccess={onGoogleSuccess}
      render={({ onClick }) => <GoogleButtonFace onClick={onClick} loading={googleLoading} short={cardShort} roomy={roomy} />}
    />
  );

  const card = (
    <AuthCard flow={flow} stepIndex={stepIndex} mobile={mobile} short={cardShort} roomy={roomy}>
      {step === "details" && (
        <StepDetails
          mobile={mobile}
          short={cardShort}
          roomy={roomy}
          showChips={!narrow && !veryShort}
          name={name}
          email={email}
          onNameChange={setName}
          onEmailChange={setEmail}
          promoCode={promoCode}
          onPromoChange={setPromoCode}
          loading={loading}
          error={sendError}
          notice={notice}
          flow={flow}
          onSwitchFlow={() => switchFlow()}
          onSend={sendCode}
          googleButton={googleButton}
        />
      )}
      {step === "verify" && (
        <StepVerify
          mobile={mobile}
          short={cardShort}
          roomy={roomy}
          first={name.trim() ? first : ""}
          email={email}
          otp={otp}
          onOtpChange={(next) => {
            setOtp(next);
            setOtpError("");
          }}
          error={otpError}
          errorCount={otpErrorCount}
          loading={loading}
          secs={secs}
          onResend={resendCode}
          onBack={() => {
            setOtpError("");
            setStep("details");
          }}
          onVerify={verifyCode}
        />
      )}
      {step === "about" && (
        <StepAbout
          mobile={mobile}
          short={cardShort}
          roomy={roomy}
          first={first}
          showName={askName}
          name={name}
          onNameChange={setName}
          {...about}
          onChange={(patch) => setAbout((a) => ({ ...a, ...patch }))}
          loading={loading}
          error={aboutError}
          onStart={saveAbout}
          onSkip={skipAbout}
        />
      )}
      {step === "done" && (
        <StepDone mobile={mobile} short={cardShort} first={first} initials={initials} company={about.company} skipped={skipped} onContinue={finish} />
      )}
    </AuthCard>
  );

  if (mobile) {
    return (
      <div className="ds-auth" style={{ display: "flex", flexDirection: "column", minHeight: "100dvh", width: "100%", overflowX: "hidden", fontFamily: FONT, background: "#FDF6F9" }}>
        <div style={{ position: "relative", zIndex: 1, flex: "none" }}>
          <ProductDemoPanel compact mini={step !== "details"} />
        </div>
        <div style={{ position: "relative", zIndex: 2, display: "flex", flexDirection: "column", alignItems: "center", padding: "0 14px 20px", marginTop: -28 }}>
          {card}
        </div>
      </div>
    );
  }

  return (
    <div className="ds-auth" style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100dvh", minHeight: 640, overflow: "hidden", fontFamily: FONT, background: "#FDF6F9" }}>
      {/* On big screens the logo, form and panel stay together in a centred 1600 x 1000 frame. */}
      <div style={{ position: "relative", display: "flex", width: "100%", maxWidth: 1600, height: "100%", maxHeight: 1000 }}>
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <div className="ds-orb" style={{ position: "absolute", top: -80, left: -120, width: 420, height: 420, borderRadius: "50%", background: "radial-gradient(circle,rgba(201,94,190,.34),rgba(201,94,190,0) 70%)", filter: "blur(18px)", animation: "dsOrbA 14s ease-in-out infinite" }} />
          <div className="ds-orb" style={{ position: "absolute", bottom: -140, left: 120, width: 460, height: 460, borderRadius: "50%", background: "radial-gradient(circle,rgba(111,82,237,.22),rgba(111,82,237,0) 70%)", filter: "blur(22px)", animation: "dsOrbB 18s ease-in-out infinite" }} />
        </div>
        <div style={{ position: "relative", flex: 1, minWidth: 0, overflow: "hidden" }}>
          <img src="/docusensasign.svg" alt="DocuSensa" style={{ position: "absolute", top: 28, left: 32, zIndex: 3, width: 165, height: 30, objectFit: "contain", display: "block" }} />
          <div style={{ position: "absolute", inset: 0, zIndex: 2, overflowY: "auto", display: "flex", flexDirection: "column", padding: short ? "72px 24px 20px" : "84px 24px 32px" }}>
            <div style={{ margin: "auto" }}>{card}</div>
          </div>
        </div>
        <div style={{ position: "relative", flex: "0 0 44%", maxWidth: 780, height: "calc(100% - 40px)", maxHeight: 960, alignSelf: "center", marginRight: 20, display: "flex" }}>
          <ProductDemoPanel short={short} />
        </div>
      </div>
    </div>
  );
};

export default Signin;
